const SESSION_TOKEN_KEY = 'sessionToken';
const USER_DATA_KEY = 'userData';
const OUTLET_CACHE_PREFIX = 'primefit:scoped-outlets:';
const OUTLET_CACHE_TTL_MS = 5 * 60 * 1000;

const canUseStorage = () => typeof window !== 'undefined';

const decodeBase64Url = value => {
  if (!value || !canUseStorage()) {
    return '';
  }

  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padding = normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4));

  try {
    return window.atob(`${normalized}${padding}`);
  } catch (error) {
    return '';
  }
};

export const parseJwtPayload = token => {
  const parts = String(token || '').split('.');
  if (parts.length < 2) {
    return null;
  }

  const decoded = decodeBase64Url(parts[1]);
  if (!decoded) {
    return null;
  }

  try {
    return JSON.parse(decoded);
  } catch (error) {
    return null;
  }
};

export const buildAdminUserFromToken = token => {
  const payload = parseJwtPayload(token);
  if (!payload) {
    return null;
  }

  return {
    userid: payload.userid ?? 0,
    username: payload.username || '',
    actor_type: payload.actor_type || '',
    full_name: payload.full_name || payload.username || '',
    role: payload.role || '',
    role_code: payload.role_code ?? 0,
    outlet_id: payload.outlet_id ?? 0,
    outlet_name: payload.outlet_name || '',
    outlet_type: payload.outlet_type || '',
    allowed_paths: Array.isArray(payload.allowed_paths) ? payload.allowed_paths : [],
  };
};

export const readStoredSessionToken = () => {
  if (!canUseStorage()) {
    return '';
  }
  return localStorage.getItem(SESSION_TOKEN_KEY) || '';
};

export const getSessionTokenRemainingMs = token => {
  const payload = parseJwtPayload(token);
  const expiresAt = Number(payload?.exp || 0) * 1000;
  if (!expiresAt) {
    return 0;
  }
  return expiresAt - Date.now();
};

export const isSessionTokenExpired = (token, bufferMs = 0) =>
  getSessionTokenRemainingMs(token) <= bufferMs;

export const shouldRefreshSessionToken = (token, bufferMs = 15 * 60 * 1000) => {
  const remainingMs = getSessionTokenRemainingMs(token);
  return remainingMs > 0 && remainingMs <= bufferMs;
};

export const storeAdminSession = ({ token, user } = {}) => {
  if (!canUseStorage()) {
    return;
  }

  if (token) {
    localStorage.setItem(SESSION_TOKEN_KEY, token);
  }

  const nextUser = user || buildAdminUserFromToken(token);
  if (nextUser) {
    let existingUser = {};
    try {
      existingUser = JSON.parse(localStorage.getItem(USER_DATA_KEY) || '{}');
    } catch (error) {
      existingUser = {};
    }

    localStorage.setItem(
      USER_DATA_KEY,
      JSON.stringify({
        ...existingUser,
        ...nextUser,
      })
    );
  }
};

export const clearAdminSession = () => {
  if (!canUseStorage()) {
    return;
  }

  localStorage.removeItem(SESSION_TOKEN_KEY);
  localStorage.removeItem(USER_DATA_KEY);

  try {
    const keysToRemove = [];
    for (let index = 0; index < sessionStorage.length; index += 1) {
      const key = sessionStorage.key(index);
      if (key && key.startsWith(OUTLET_CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  } catch (error) {
    // Ignore storage cleanup issues.
  }
};

const buildOutletCacheKey = currentUser => {
  const userKey =
    currentUser?.userid ??
    currentUser?.Userid ??
    currentUser?.id ??
    currentUser?.ID ??
    currentUser?.username ??
    currentUser?.Username ??
    'anonymous';

  return `${OUTLET_CACHE_PREFIX}${String(userKey)}`;
};

export const readCachedScopedOutlets = currentUser => {
  if (!canUseStorage()) {
    return [];
  }

  try {
    const raw = sessionStorage.getItem(buildOutletCacheKey(currentUser));
    if (!raw) {
      return [];
    }

    const payload = JSON.parse(raw);
    if (!Array.isArray(payload?.records)) {
      return [];
    }

    if (Date.now() - Number(payload.saved_at || 0) > OUTLET_CACHE_TTL_MS) {
      sessionStorage.removeItem(buildOutletCacheKey(currentUser));
      return [];
    }

    return payload.records;
  } catch (error) {
    return [];
  }
};

export const cacheScopedOutlets = (currentUser, records) => {
  if (!canUseStorage() || !Array.isArray(records) || records.length === 0) {
    return;
  }

  try {
    sessionStorage.setItem(
      buildOutletCacheKey(currentUser),
      JSON.stringify({
        saved_at: Date.now(),
        records,
      })
    );
  } catch (error) {
    // Ignore cache write issues.
  }
};
